/**
 * The audio side of Quantis Voice, behind one interface so the phone screens
 * are the same on every platform. The browser drives the calling SDK directly;
 * the Mac app runs that same browser engine inside a hidden web view, because
 * React Native on macOS has no WebRTC of its own. The wire format between the
 * app and that web view is the message union below.
 */

export type VoiceCallState = 'connecting' | 'ringing' | 'open' | 'closed';

export type VoiceEngineEvent =
  | { type: 'ready' }
  | { type: 'registered' }
  | { type: 'unregistered' }
  | { type: 'incoming'; callId: string; from: string; callerName: string | null; lineName?: string | null }
  | { type: 'state'; callId: string; state: VoiceCallState; direction: 'inbound' | 'outbound' }
  | { type: 'muted'; muted: boolean }
  | { type: 'tokenWillExpire' }
  | { type: 'playback'; playing: boolean }
  | { type: 'error'; message: string; code?: number };

export type VoiceEngineCommand =
  | { type: 'start'; token: string }
  | { type: 'updateToken'; token: string }
  | { type: 'call'; to: string }
  | { type: 'accept'; callId: string }
  | { type: 'reject'; callId: string }
  | { type: 'hangup' }
  | { type: 'mute'; muted: boolean }
  | { type: 'digits'; digits: string }
  | { type: 'play'; url: string }
  | { type: 'stopPlayback' }
  /** Reads a prompt aloud for an admin to check its wording. */
  | { type: 'speak'; text: string }
  | { type: 'stop' };

export interface VoiceEngine {
  send: (command: VoiceEngineCommand) => void;
  subscribe: (listener: (event: VoiceEngineEvent) => void) => () => void;
}

const COMMAND_TYPES = new Set<VoiceEngineCommand['type']>([
  'start', 'updateToken', 'call', 'accept', 'reject', 'hangup', 'mute', 'digits', 'play', 'stopPlayback', 'speak', 'stop',
]);
const EVENT_TYPES = new Set<VoiceEngineEvent['type']>([
  'ready', 'registered', 'unregistered', 'incoming', 'state', 'muted', 'tokenWillExpire', 'playback', 'error',
]);

/** A bridge message is a JSON object whose `channel` marks it as ours; anything else is dropped. */
const CHANNEL = 'quantis-voice';

export const encodeVoiceMessage = (message: VoiceEngineCommand | VoiceEngineEvent) =>
  JSON.stringify({ channel: CHANNEL, ...message });

function parse(data: unknown): Record<string, unknown> | null {
  if (typeof data !== 'string') return null;
  try {
    const value = JSON.parse(data);
    return value && typeof value === 'object' && value.channel === CHANNEL ? value : null;
  } catch {
    return null;
  }
}

export function parseVoiceCommand(data: unknown): VoiceEngineCommand | null {
  const value = parse(data);
  if (!value || !COMMAND_TYPES.has(value.type as VoiceEngineCommand['type'])) return null;
  const { channel: _channel, ...command } = value;
  return command as unknown as VoiceEngineCommand;
}

export function parseVoiceEvent(data: unknown): VoiceEngineEvent | null {
  const value = parse(data);
  if (!value || !EVENT_TYPES.has(value.type as VoiceEngineEvent['type'])) return null;
  const { channel: _channel, ...event } = value;
  return event as unknown as VoiceEngineEvent;
}

/** A tiny listener set, shared by the engines. */
export function createEmitter() {
  const listeners = new Set<(event: VoiceEngineEvent) => void>();
  return {
    emit: (event: VoiceEngineEvent) => listeners.forEach((listener) => listener(event)),
    subscribe: (listener: (event: VoiceEngineEvent) => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}
